import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronRight, ClipboardList, FileJson, FileText, History, ListOrdered, LockKeyhole, Sheet, Users } from "lucide-react";
import { directorPath, publicPath } from "./directorPortal";
import { matchPresentationPhase } from "./matchPresentation";
import DirectorReportDownload from "./DirectorReportDownload";
import "./DirectorReports.css";

export default function DirectorReports({ match }) {
  const [params] = useSearchParams();
  const document = params.get("document");
  const heading = useRef(null);
  useEffect(() => { if (document) heading.current?.focus(); }, [document]);
  const path = directorPath(match);
  const publicPage = publicPath(match);
  const bullseye = match.family === "bullseye";
  const phase = matchPresentationPhase(match);
  const resultsUnavailable = !["in_progress", "completed"].includes(phase);
  const resultsAvailability = phase === "cancelled" ? "Unavailable for cancelled matches." : "Available once the match starts.";
  const returnState = { directorPath: path };
  const reportPath = document => `${path}/reports?document=${document}`;
  const groups = [
    {
      id: "results", title: "Results", rows: bullseye ? [
        { document: "standings", label: "Standings", format: "PDF", icon: ListOrdered, to: reportPath("standings") },
        { document: "bulletin", label: "Results bulletin", format: "PDF", icon: FileText, to: reportPath("bulletin") },
      ] : [
        { label: "Standings", format: "View", icon: ListOrdered, to: publicPage },
      ],
    },
    {
      id: "operations", title: "Match operations", rows: [
        ...(bullseye ? [{ document: "relays", label: "Relay assignments", format: "PDF", icon: Users, to: reportPath("relays") }] : []),
        { document: "program", label: "Match program", format: "PDF", icon: ClipboardList, to: reportPath("program") },
      ],
    },
    {
      id: "official", title: "Official reporting", rows: [bullseye
        ? { label: "NRA score reporting", format: "PDF / CSV", icon: FileText, to: `${path}/nra-reporting`, private: true }
        : { label: "NRA ARC report", format: "JSON", icon: FileJson, to: `/leaderboards/manage/${encodeURIComponent(match.matchId)}/registrations`, private: true },
        { label: "Distinguished report", format: "Excel", icon: Sheet, to: `${path}/distinguished-report`, private: true }],
    },
    {
      id: "audit", title: "Audit & data", rows: [
        { label: "Scoring action history", format: "JSON", icon: History, to: `${path}/audit`, private: true },
      ],
    },
  ];
  const selectedGroup = document && groups.find(group => group.rows.some(row => row.document === document));
  const selected = selectedGroup?.rows.find(row => row.document === document);
  if (document) return <div className="director-reports">
    <Link className="dp-back" to={`${path}/reports`}><ArrowLeft size={15} aria-hidden="true" />All reports</Link>
    <h2 ref={heading} tabIndex={-1}>{selected?.label || "Report unavailable"}</h2>
    {!selected ? <p>This report is not available for this match.</p>
      : selectedGroup.id === "results" && resultsUnavailable ? <p role="status">{resultsAvailability}</p>
        : <div className="director-report-download"><DirectorReportDownload key={`${match.matchId}:${document}`} match={match} document={document} /></div>}
  </div>;

  return <div className="director-reports">
    <h2>Reports</h2>
    {groups.map(group => {
      const unavailable = group.id === "results" && resultsUnavailable;
      const Row = unavailable ? "span" : Link;
      return <section key={group.id} className="director-reports-group" aria-labelledby={`director-reports-${group.id}`}>
      <div><h3 id={`director-reports-${group.id}`}>{group.title}</h3>
        {unavailable && <p className="director-reports-availability" id="director-reports-availability">{resultsAvailability}</p>}
      </div>
      <ul className="director-reports-list">
        {group.rows.map(({ label, format, icon: Icon, to, document, private: privateReport }) => <li key={label}>
          <Row className="director-reports-row" {...(unavailable
            ? { role: "link", "aria-disabled": true, "aria-describedby": "director-reports-availability" }
            : { to, state: privateReport ? undefined : returnState })}>
            <Icon className="director-reports-icon" size={19} aria-hidden="true" />
            <span className="director-reports-name">{label}{privateReport && <span className="director-reports-access"><LockKeyhole size={12} aria-hidden="true" />Director only</span>}</span>
            <span className="director-reports-format">{format}</span>
            {!unavailable && (privateReport || document ? <ChevronRight className="director-reports-arrow" size={17} aria-hidden="true" /> : <ArrowUpRight className="director-reports-arrow" size={17} aria-hidden="true" />)}
          </Row>
        </li>)}
      </ul>
    </section>; })}
  </div>;
}
